import { useThrottleFn } from '@vueuse/core'
import { useDragAndDropStore } from '../stores/drag-and-drop'
import { useCancelableDebounceFn } from './useCancelableDebounceFn'
import { DropZoneInjectKey, useDragItemContext } from '~/components/utils/DragAndDrop/context'

function randonID() {
  return Math.random().toString(36).slice(2)
}
interface Options<T> {
  container: Ref<HTMLDivElement | null> | ComputedRef<HTMLDivElement | null>
  previewArea?: Ref<HTMLDivElement | null>
  previewComponent?: Ref<HTMLDivElement | null>
  onDrop: (data: T, index: number) => void
  onMove: (from: number, to: number) => void
  disabled: ComputedRef<boolean>
}

export function useDropzone<T>(
  options: Options<T>,
) {
  const { setHoverZoneId } = useDragAndDropStore()
  const draggableContext = useDragItemContext()
  const id = randonID()

  const dragged = ref(false)
  const hover = ref(false)
  const childrenHoverIndex = ref(-1)
  const childrenHoverTop = ref(false)
  const previewElement = computed(() => options.previewComponent?.value || null)
  const moviment = reactive({ x: 0, y: 0, lastX: 0, lastY: 0 })

  const disabled = computed(() => {
    return options.disabled.value || !!draggableContext?.disabled.value || draggableContext?.isDragged.value === true
  })

  const reset = useCancelableDebounceFn(() => {
    const item = options.container.value

    if (!item)
      return
    hover.value = false
    setHoverZoneId(null)
    item.classList.remove('hover')
  }, 300)

  const movePreview = (position: 'top' | 'bottom', target?: HTMLDivElement) => {
    reset.cancel()
    try {
      if (!previewElement.value || !options.previewArea?.value)
        return
      if (!target) {
        options.previewArea?.value?.appendChild(previewElement.value)
        return
      }
      previewElement.value.style.visibility = 'visible'
      if (position === 'top')
        options.previewArea?.value?.insertBefore(previewElement.value, target)

      if (position === 'bottom') {
        if (target.nextSibling) {
          options.previewArea?.value?.insertBefore(previewElement.value, target.nextSibling)
          return
        }
        options.previewArea?.value?.appendChild(previewElement.value)
      }
    }
    catch (error) {
    }
  }

  const updatePreview = (event: DragEvent) => {
    const childrens = options.previewArea?.value?.querySelectorAll(':scope > .dragable') as NodeListOf<HTMLDivElement>
    if (!childrens)
      return
    if (childrens.length === 0)
      return
    const { hoverIndex, onTop } = getIndexByProximity(childrens, event)
    if (hoverIndex === childrenHoverIndex.value && onTop === childrenHoverTop.value)
      return
    childrenHoverIndex.value = hoverIndex
    childrenHoverTop.value = onTop
    movePreview(onTop ? 'top' : 'bottom', childrens[hoverIndex])
  }

  const updateState = useThrottleFn((event: DragEvent) => {
    reset.cancel()
    hover.value = true
    if (event.type === 'dragenter')
      window.dispatchEvent(new CustomEvent('drgg:dragenter', { detail: { id } }))

    setHoverZoneId(id)
    updatePreview(event)
  }, 100)

  const setChildrenDragged = (_: number) => {
    dragged.value = true
  }

  const removeChildrenDragged = () => {
    dragged.value = false
  }

  const validLeave = (event: DragEvent) => {
    const item = options.container.value
    if (!item)
      return

    const rect = item.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right
      || event.clientY < rect.top || event.clientY > rect.bottom)
      reset.force()
  }

  const handlerDrop = (e: DragEvent) => {
    if (disabled.value)
      return
    e.stopPropagation()
    e.preventDefault()
    const data = e.dataTransfer?.getData('text/json')

    if (!data) {
      reset.force()
      return
    }

    const dragdataToJSON = JSON.parse(data) as { zoneId: string; index: number; data: T }

    const isChildren = dragdataToJSON.zoneId === id

    if (isChildren) {
      const index = dragdataToJSON.index
      if (index === childrenHoverIndex.value)
        return
      options.onMove(index, childrenHoverTop.value ? childrenHoverIndex.value : childrenHoverIndex.value + 1)
      reset.force()
      return
    }
    options.onDrop(dragdataToJSON.data, childrenHoverTop.value ? childrenHoverIndex.value : childrenHoverIndex.value + 1)
    reset.force()
  }

  const handlerDragEnter = (e: DragEvent) => {
    if (disabled.value)
      return
    e.preventDefault()
    e.stopPropagation()

    reset.cancel()
    moviment.lastX = e.clientX
    moviment.lastY = e.clientY
    updateState(e)
  }

  const handlerDragHover = (e: DragEvent) => {
    if (disabled.value)
      return
    e.preventDefault()
    e.stopPropagation()

    moviment.x = e.clientX - moviment.lastX
    moviment.y = e.clientY - moviment.lastY
    moviment.lastX = e.clientX
    moviment.lastY = e.clientY

    if (moviment.x === 0 && moviment.y === 0)
      return

    reset.cancel()
    updateState(e)
  }

  const handlerDragLeave = (e: DragEvent) => {
    if (disabled.value)
      return

    e.stopPropagation()
    e.preventDefault()
    reset()
    validLeave(e)
  }

  function bindContainerEvents() {
    if (!options.container.value)
      return

    const item = options.container.value
    item.addEventListener('dragover', handlerDragHover)
    item.addEventListener('dragleave', handlerDragLeave)
    item.addEventListener('dragenter', handlerDragEnter)
    item.addEventListener('drop', handlerDrop)
  }

  onBeforeUnmount(() => {
    const item = options.container.value
    if (!item)
      return

    item.removeEventListener('dragover', handlerDragHover)
    item.removeEventListener('dragleave', handlerDragLeave)
    item.removeEventListener('dragenter', handlerDragEnter)
    item.removeEventListener('drop', handlerDrop)
  })

  onMounted(() => {
    bindContainerEvents()
  })

  provide(DropZoneInjectKey, {
    id,
    isHover: hover,
    setChildrenDragged,
    removeChildrenDragged,
    disabled,
  })

  return {
    index: childrenHoverIndex,
    isOnTop: childrenHoverTop,
    isHover: hover,
    zoneId: id,
    isChildrenDragged: dragged,
    setChildrenDragged,
    removeChildrenDragged,
    disabled,
    draggableContext,
  }
}
